<template>
  <lf-toast />
  <test-mode-alert v-if="inTestMode" />
  <div id="app-dashboard" class="flex h-screen w-screen">
    <dashboard-nav v-if="!isPublicRoute && user?.id" :test-mode="inTestMode" />
    <div
      id="appWrapper"
      class="flex-1 flex-col overflow-auto"
      :class="{ 'pt-12': inTestMode }"
    >
      <loader :is-loading="isGlobalLoading" />
      <router-view />
    </div>
    <transition name="slide-fade-deal-activity">
      <deal-activity
        v-if="showDealActivity && (canSeeDealActivity || isDealsList)"
        class="fixed top-0 right-0 z-100 w-1/4 min-w-[460px] bg-white shadow-2xl"
      />
    </transition>
    <pip-component v-if="pipType" :type="pipType" @pip:hide="pipType = null" />
  </div>
  <twilio-dialer v-if="loadTwilioDialer" />
  <incoming-sms v-if="auth.isLendflowUser" />
  <shortcuts-help v-if="helpShown" @close="helpShown = false" />
</template>
<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { DealActivityState } from "@/enums/dealActivity";
import { ShortcutAction } from "@/enums/keyboardShortcuts";
import { PUBLIC_PAGES } from "@/helpers/constants";
import { DEFAULT_DEAL_ACTIVITY_OPTIONS } from "@/helpers/constants/deals";
import { logWarning } from "@/helpers/formatting";
import { loadExploScript } from "@/helpers/thirdPartyService";
import { useAuth, useImpersonate, useRefreshToken } from "@/hooks/auth";
import { useEmitter } from "@/hooks/common";
import { useFullStory } from "@/hooks/fullStory";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import { useLocalStorageSetting } from "@/hooks/options";
import { ROUTE_AI_AGENTS, ROUTE_DEALS, ROUTE_INBOX } from "@/router/routes";
import useApplicationsStore from "@/stores/applications";
import { useTwilioStore } from "@/stores/twilio";

import DashboardNav from "@/components/DashboardNav.vue";
import PipComponent from "@/components/PipComponent.vue";
import ShortcutsHelp from "@/components/ShortcutsHelp.vue";
import DealActivity from "@/components/deals/dealActivity/DealActivity.vue";
import IncomingSms from "@/components/twilio/IncomingSms.vue";
import TwilioDialer from "@/components/twilio/TwilioDialer.vue";
import LfToast from "@/components/ui/LfToast.vue";
import TestModeAlert from "@/components/ui/TestModeAlert.vue";

import type { PipType } from "@/enums/pip";
import type { IClient } from "@/models/clients";
import type { IUser } from "@/models/users";

const { initFullStory } = useFullStory();
initFullStory();

const pipType = ref<PipType | null>(null);
const inTestMode = ref(false);
const helpShown = ref(false);

const user = computed<IUser>(() => getters["auth/user"]);

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { getters, commit } = useStore();
const applicationsStore = useApplicationsStore();
const twilioStore = useTwilioStore();
const { getUserAssignedTwilioNumbers } = twilioStore;
const { userAssignedPhoneNumbers } = storeToRefs(twilioStore);
const dealActivityOptions = useLocalStorageSetting(
  "dealActivityOptions",
  DEFAULT_DEAL_ACTIVITY_OPTIONS
);
const { canSeeDealActivity } = storeToRefs(applicationsStore);
const emitter = useEmitter();
const { assignShortcutToAction } = useKeyboardShortcuts();

const auth = useAuth();
const { hasImpersonateMessage, showImpersonateMessage } = useImpersonate();

const isPublicRoute = computed(
  () => PUBLIC_PAGES.indexOf(route.name as string) > -1
);
const isDealsList = computed(() => route.name === ROUTE_DEALS);
const isGlobalLoading = computed<boolean>(() => getters["globalLoader"]);
const authClientSettings = computed<IClient>(
  () => getters["auth/authClientSettings"]
);
const loadTwilioDialer = computed(
  () => !!userAssignedPhoneNumbers.value?.length && auth.isLendflowUser
);

const showDealActivity = computed(
  () =>
    ![DealActivityState.Closed, DealActivityState.PiP].includes(
      dealActivityOptions.value.state
    )
);

emitter.on("OPEN_PIP_VIEW", (type: PipType) => {
  pipType.value = type;
});

const openDealsList = () => router.push({ name: ROUTE_DEALS });
const openInbox = () => router.push({ name: ROUTE_INBOX });
const openAiAgents = () => router.push({ name: ROUTE_AI_AGENTS });

assignShortcutToAction(
  ShortcutAction.ToggleHelp,
  () => (helpShown.value = !helpShown.value)
);

assignShortcutToAction(ShortcutAction.OpenAiAgents, openAiAgents);
assignShortcutToAction(ShortcutAction.OpenDealsList, openDealsList);
assignShortcutToAction(ShortcutAction.OpenInbox, openInbox);

watch(
  () => authClientSettings.value,
  () => {
    inTestMode.value = !!(
      authClientSettings.value?.in_test_mode &&
      authClientSettings.value.id === user.value?.client_id
    );
  }
);

watch(
  () => auth.currentUser,
  async () => {
    if (!auth.currentUser?.id) {
      return;
    }
    await getUserAssignedTwilioNumbers(auth.currentUser.id);
  },
  {
    immediate: true,
  }
);

onBeforeMount(() => {
  if (hasImpersonateMessage.value) {
    commit("setPreventGlobalMessage", true);
  }
});

onMounted(async () => {
  logWarning(
    `${t("COMMON.CONSOLE_WARNING_DESC_1")}.`,
    `${t("COMMON.CONSOLE_WARNING_DESC_2")}.`
  );
  useRefreshToken(10);

  showImpersonateMessage();
  loadExploScript();
});
</script>

<style>
@reference "@/assets/tailwind.css";

#app {
  -webkit-font-smoothing: antialiased;
}
body {
  background-color: #f1f4f7;
}
.slide-fade-deal-activity-enter-active {
  transition: all 0.3s ease-in-out;
}

.slide-fade-deal-activity-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-fade-deal-activity-enter-from {
  @apply translate-x-full transform;
  opacity: 0;
}

.slide-fade-deal-activity-enter-to,
.slide-fade-deal-activity-leave-from {
  @apply translate-x-0 transform;
  opacity: 1;
}

.slide-fade-deal-activity-leave-to {
  @apply translate-x-full transform;
  opacity: 0;
}
input:-webkit-autofill,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  box-shadow: 0 0 0 30px white inset !important;
  -webkit-box-shadow: 0 0 0 30px white inset !important;
}
.autofil-transparent:-webkit-autofill,
.autofil-transparent:-webkit-autofill:hover,
.autofil-transparent:-webkit-autofill:focus,
.autofil-transparent:-webkit-autofill:active {
  box-shadow: 0 0 0 30px transparent inset !important;
  -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  -webkit-text-fill-color: white;
  transition: all 9999s 9999s linear;
}

[type="checkbox"] {
  @apply h-4! w-4! place-self-center! rounded-xs! border-gray-300!;
}

[type="checkbox"]:disabled {
  @apply text-gray-300;
}

.action-menu-dd {
  @apply absolute z-20 w-max rounded-md bg-white py-2 text-sm text-gray-500 shadow-md focus:outline-hidden sm:text-sm;
}

.p-datatable {
  @apply overflow-hidden rounded border;
}

.p-datatable
  thead
  *:not(.p-checkbox):not(.p-checkbox-box):not(.p-checkbox-icon) {
  @apply !bg-white;
}

.p-datatable th {
  @apply font-semibold! text-gray-400!;
}

.p-datatable-header {
  @apply !bg-white;
}

.icon-chevron-up {
  background: url("@/assets/icons/Chevron.svg");
  height: 16px;
  width: 16px;
  align-self: center;
}

.icon-chevron-down {
  background: url("@/assets/icons/Chevron.svg") no-repeat;
  transform: rotate(180deg);
  height: 16px;
  width: 16px;
  align-self: center;
}

.dropdown-tooltip {
  pointer-events: none;
}

.dropdown-tooltip .p-tooltip-text {
  @apply bg-black text-xs;
}

.tippy-box[data-theme~="lendflow"] {
  @apply max-h-47 overflow-hidden bg-grey-navigation text-xs leading-5 font-normal break-words text-ellipsis;
}

.tippy-box[data-theme~="light"] {
  @apply overflow-hidden bg-headline text-xs leading-5 font-normal break-words text-white;

  & .tippy-arrow {
    @apply text-headline;
  }
}

.tippy-box[data-theme~="none"] {
  @apply overflow-hidden rounded-none bg-transparent text-sm leading-normal text-gray-400;

  & .tippy-content {
    @apply p-0;
  }
}

.tippy-box[data-theme~="tour"] {
  @apply overflow-y-auto bg-[#7800D3] text-xs leading-5 font-normal break-words;

  & .tippy-arrow {
    @apply text-[#7800D3];
  }

  & button {
    @apply bg-[#5a188d] text-white;
  }
}
.ql-align-right {
  @apply text-right;
}
.ql-align-center {
  @apply text-center;
}
.ql-align-justify {
  @apply text-justify;
}
.ql-align-left {
  @apply text-left;
}
</style>
